/**
 * @flow
 */

import React from 'react';
import gql from 'graphql-tag';
import { format, startOfDay } from 'date-fns';
import { useQuery } from '@apollo/client';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Loader } from 'semantic-ui-react';
import { map, property, get } from 'lodash';

import Timetable from '../../../../../components/timetable/Timetable';
import WorkingTimeCell from './WorkingTimeCell';
import WorkingTimeCard from './WorkingTimeCard';
import { Header } from '../../../../../components/timetable';
import { MasterTypeIcon } from '../../../../../components';
import { setMaster } from '../../../../../actions/master-appointments';

import type { Salon, Master } from '../../../../../type';

type RenderCardsProps = {
    master: Master,
    salon: Salon,
};

// The query gets salon details
export const GET_SALON_QUERY = gql`
    query getSalon($date: String!, $salon: ID!, $lang: String!) {
        viewer {
            id
            ... on Administrator {
                salon(id: $salon) {
                    id
                    settings {
                        schedule {
                            startAt
                            endAt
                            step
                            format
                        }
                    }
                    masters {
                        nodes {
                            id
                            ...master
                        }
                    }
                }
            }
        }
    }

    fragment master on Master {
        firstName
        lastName
        phone
        email
        status
        type
        serviceGroups {
            id
            name(lang: $lang)
            type
        }
        schedule(startDate: $date) {
            id
            startAt
            endAt
        }
    }
`;

/**
 * Return list of cards with working times for provided master
 */
const renderCards = ({ provider, salon }: RenderCardsProps) => {
    return map(provider.schedule, workingTime => (
        <WorkingTimeCard
            key={workingTime.id}
            workingTime={workingTime}
            schedule={salon.settings.schedule}
            master={provider}
        />
    ));
};

const ScheduleTimetable = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const date = useSelector(({ schedule }) => schedule.get('date'));
    const salonId = useSelector(({ user }) => user.get('salon'));
    const currentLanguage = useSelector(
        ({ intl }) => intl.get('locale') || intl.get('defaultLanguage'),
    );

    const { loading, data } = useQuery(GET_SALON_QUERY, {
        variables: {
            salon: salonId,
            date: format(new Date(date), 'yyyy-MM-dd'),
            lang: currentLanguage,
        },
        fetchPolicy: 'cache-and-network',
    });

    const salon = get(data, 'viewer.salon');

    const onHeaderClick = master => {
        dispatch(setMaster(master));
        history.push('/salon/master-appointments');
    };

    // Show spinner until the salon (with masters/schedule) has loaded
    if (!salon) {
        return <Loader size="big" inline="centered" active />;
    }

    return (
        <Timetable
            loading={loading}
            schedule={salon.settings.schedule}
            providers={salon.masters.nodes}
            scrollProps={{ scrollableGrid: true, minColumnWidth: true }}
            date={startOfDay(new Date(date))}
            renderCell={props => (
                <WorkingTimeCell key={props.time} {...props} />
            )}
            renderCards={({ provider }) => renderCards({ provider, salon })}
            renderHeader={({ provider }) => (
                <Header
                    provider={provider}
                    headerText={`${provider.firstName} ${provider.lastName ||
                        ''}`}
                    onHeaderClick={onHeaderClick}
                >
                    <MasterTypeIcon
                        name={`${provider.firstName} ${provider.lastName ||
                            ''}`}
                        type={property(['serviceGroups', '0', 'type'])(
                            provider,
                        )}
                    />
                </Header>
            )}
        />
    );
};

export default ScheduleTimetable;
